import _ from "lodash";
import React, {
  useState,
  useRef,
  useMemo,
  useCallback,
  useEffect,
} from "react";

import classes from "./TagLearningGoals.scss";
import {
  Modal,
  Button,
  Checkbox,
  DSProvider,
  IconButton,
  Tooltip,
  SearchInput,
  ChecklistDropdown,
  FilterLabelButton,
} from "@toddle-edu/ds-web";
import { colors, fontStyle } from "Constants";
import { connect } from "react-redux";
import { useTranslation } from "react-i18next";
import { compose } from "lib/apolloClient";
import TagLearningGoalsList from "./TagLearningGoalsList";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";

import {
  getIsNewGoalTaggingEnabled,
  getTaggedUnitNameFromAssesment,
  getTaggedUnitNames,
  isAssessmentTagged,
  isEvidencingEditRoute,
  isUnitPlanOrAssessmentTagged,
  getIsNewGradingSetupEnabled,
  getIsSubjectStandardsFilterEnabled,
  getIsStandardsRevampEnabled,
} from "Post/utils";
import { ChevronRightOutlined } from "@toddle-edu/ds-icons";
import { DEFAULT_STANDARD_FILTERS } from "./Utils";
import { getLabelValueForMultiFilterLabelButton } from "Utils";
import ThreeBlockSkeletonLoader from "./components/ThreeBlockSkeletonLoader";

const BLANK_OBJECT = {};
const BLANK_ARRAY = [];

const styles = {
  unitCountStyle: { textDecoration: "underline", cursor: "pointer" },
  overlayInnerStyle: {
    maxWidth: "188px",
    overflowY: "auto",
    whiteSpace: "pre-wrap",
  },
  labelStyle: {
    fontStyle: fontStyle.demiBold,
    color: colors.textDefault,
    fontSize: "1.4rem",
    lineHeight: "2rem",
  },
  nodeSpanStyle: {
    fontStyle: fontStyle.medium,
    color: colors.textOnDisabled,
    fontSize: "1.4rem",
    lineHeight: "2rem",
  },
  modalBody: {
    overflowX: "hidden",
    overflowY: "auto",
    margin: 0,
    padding: 0,
    borderTop: ` 1px solid ${colors.strokeTwo}`,
    borderBottom: ` 1px solid ${colors.strokeTwo}`,
  },
  modalHeader: {
    display: "flex",
    width: "100%",
    position: "relative",
    justifyContent: "space-between",
    alignItems: "center",
  },
  modalHeaderV2: {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  modalHeaderTitle: {
    display: "flex",
    alignItems: "center",
    marginRight: "8px",
    width: "33%",
  },
  modalFooter: {
    display: "flex",
    width: "100%",
    minHeight: "72px",
    position: "relative",
    justifyContent: "space-between",
    alignItems: "center",
  },
  configModalBody: {
    padding: "0px 32px",
  },
  modalFooterText: count => {
    return {
      paddingRight: count ? "8px" : "0px",
      borderRight: count ? `1px solid ${colors.strokeTwo}` : "none",
      marginLeft: "8px",
    };
  },
  postType: {
    color: colors.textOnDisabled,
    fontSize: "1.5rem",
    lineHeight: "2rem",
    fontWeight: "500",
  },
  imagePlaceholder: {
    height: "24px",
    width: "24px",
    display: "inline-block",
    borderRadius: "4px",
    marginRight: "4px",
    backgroundSize: "cover",
  },
  learningGoalsStyle: { paddingInline: "0px" },
};

function TagLearningGoals(props) {
  const { t } = useTranslation(["journal", "common"]);
  const {
    props: screensProps,
    modalHeaderSubTitle,
    type,
    selectFromPost,
    goToPreviousScreen,
    unitPlan,
    resourceItem,
    postTaggedEntity,
    elementPYP,
    standardsTagFilter,
    selectFromAllPYP,
  } = props;
  const { onCloseClick, updatePost } = screensProps;
  const [selectedElementsCount, setSelectedElementsCount] = useState(0);
  const [searchText, setSearchText] = useState("");
  const [isSelectAllChecked, setIsSelectAllChecked] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [isStateChanged, setIsStateChanged] = useState(false);
  const [showAlert, setAlert] = useState(false);
  const [showDiscardAlert, setDiscardAlert] = useState(false);
  const [showCourseStandards, setShowCourseStandards] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const toggleCourseStandards = () => {
    setShowCourseStandards(!showCourseStandards);
  };

  useEffect(() => {
    return () => {
      updatePost({ standardFilters: DEFAULT_STANDARD_FILTERS });
    };
  }, []);

  const [
    showPreviousScreenDiscardAlert,
    setPreviousScreenDiscardAlert,
  ] = useState(false);
  const listRef = useRef();
  const selectElementsRef = useRef();
  const modalref = useRef();
  const contentRef = useRef();
  const parentContainerRef = useRef();
  const filtersRef = useRef();
  const [standardFilters, setStandardFilters] = useState(() => {
    // Using a function in useState ensures we're creating a new object instance
    return { ...DEFAULT_STANDARD_FILTERS };
  });
  const updateSelectedElementsCount = selectedElementsCount => {
    setSelectedElementsCount(selectedElementsCount);
  };
  const isAssessment = isAssessmentTagged(postTaggedEntity);
  const updateTotalCount = count => {
    setTotalCount(count);
  };

  const updateShowFilters = showFilters => {
    setShowFilters(showFilters);
  };

  const onNextClick = () => {
    if (_.isFunction(listRef.current)) {
      listRef.current();
    }
  };

  const updateStateChange = state => {
    setIsStateChanged(state);
  };

  const onSelectGoals = () => {
    if (_.isFunction(selectElementsRef.current)) {
      selectElementsRef.current();
    }
  };

  const onModalClose = () => {
    onCloseClick();
    selectFromPost &&
      updatePost({
        selectFromPost: false,
        selectedPypElement: undefined,
        standardsTagFilter: [],
      });
    updatePost({
      showTagLearningGoalsModal: false,
      isTaggedFromRecentPost: false,
      standardsTagFilter: [],
      selectedPypElement: undefined,
    });
  };

  const onPreviousButtonClick = () => {
    _.isFunction(goToPreviousScreen) && goToPreviousScreen();
    updatePost({
      // clear main properties of learning goals
      unitPlan: null,
      isResourceSkipped: false,
      resourceId: null,
      resourceType: null,
      resourceItem: undefined,
      elementPYP:
        !_.isEmpty(elementPYP) &&
        isUnitPlanOrAssessmentTagged(postTaggedEntity) &&
        isNewGoalTaggingEnabled
          ? elementPYP
          : undefined,
      // Clear properties used for backup
      originalUnitPlan: null,
      originalIsResourceSkipped: false,
      originalResourceId: null,
      originalResourceType: null,
      originalResourceItem: undefined,
      originalElementPYP: undefined,
      selectFromAllPYP: undefined,
      // clear ratings as well
      ibPYPElementRatings: [],
      originalibPYPElementRatings: [],
      isTaggedFromRecentPost: false,
    });
  };

  const onClose = (forceClose = false) => {
    if (forceClose) {
      setAlert(false);
      setIsStateChanged(false);
      onModalClose();
      return;
    }
    if (isStateChanged) {
      setAlert(true);
    } else {
      onModalClose();
    }
  };

  const updateSearchText = text => {
    setSearchText(text);
  };
  const handleClickOutside = e => {
    if (
      contentRef.current &&
      !contentRef.current.contains(e.target) &&
      !isNewGoalTaggingEnabled
    ) {
      e?.stopPropagation();
      setDiscardAlert(true);
    }
  };
  const isNewGoalTaggingEnabled = getIsNewGoalTaggingEnabled();
  const isNewGoalTaggingEnabledAndResourceTagged =
    isNewGoalTaggingEnabled && isUnitPlanOrAssessmentTagged(postTaggedEntity);
  const selectLEFooterText = useMemo(() => {
    const unitNames = getTaggedUnitNames(postTaggedEntity);
    if (
      isNewGoalTaggingEnabled &&
      !_.isEmpty(unitNames) &&
      !isAssessmentTagged(postTaggedEntity)
    ) {
      return _.size(unitNames) > 1 ? (
        <div>
          {t("journal:select_learning_goals_from_units")}
          <Tooltip
            tooltip={_.join(unitNames, ",")}
            placement={"topLeft"}
            showArrow={true}
            overlayInnerStyle={styles.overlayInnerStyle}
          >
            <span style={styles.unitCountStyle}>{`${_.size(
              unitNames
            )} units`}</span>
          </Tooltip>
        </div>
      ) : (
        t("journal:select_all_learning_goals_from_unit", {
          unitName: _.first(unitNames),
        })
      );
    } else if (
      isNewGoalTaggingEnabled &&
      isAssessmentTagged(postTaggedEntity)
    ) {
      const unitName = getTaggedUnitNameFromAssesment(postTaggedEntity);
      return t("journal:select_all_learning_goals_from_unit", {
        unitName: unitName,
      });
    } else {
      return t("journal:select_all_learning_goals");
    }
  }, [isNewGoalTaggingEnabled, postTaggedEntity]);

  const onPreviousOrCancelClick = () => {
    if (isNewGoalTaggingEnabled) {
      onPreviousButtonClick();
      onSelectGoals();
    } else {
      selectedElementsCount
        ? !!unitPlan || !!resourceItem
          ? setPreviousScreenDiscardAlert(true)
          : setDiscardAlert(true)
        : !!unitPlan || !!resourceItem
        ? onPreviousButtonClick()
        : onClose();
    }
  };

  const tagOptions = useMemo(() => {
    if (filtersRef) {
      const filterOptions = filtersRef.current;
      return _.map(filterOptions, tag => {
        return { label: _.get(tag, "label"), value: _.get(tag, "id") };
      });
    }
    return [];
  }, [filtersRef.current]);

  const onChangeFilter = useCallback(
    values => {
      updatePost({ standardsTagFilter: values });
    },
    [updatePost]
  );

  const closeAlert = () => setAlert(false);
  const onCloseModal = () => onClose(true);
  const closeDiscardAlert = e => () => {
    e.stopPropagation();
    setDiscardAlert(false);
  };
  const onClickDisacardAndStartOver = () => {
    isNewGoalTaggingEnabled ? onPreviousButtonClick() : onClose(true);
  };
  const disableUnitNavigation =
    isEvidencingEditRoute() && !isAssessmentTagged(postTaggedEntity);
  const isGradingSetupEnabled = getIsNewGradingSetupEnabled();
  const isSubjectStandardsFilterEnabled = getIsSubjectStandardsFilterEnabled();

  const CustomLoadingComponent = () => {
    return (
      <ThreeBlockSkeletonLoader
        leftBlockWidth="280px"
        rightBlockWidth="320px"
        leftBlockItems={6}
        middleBlockItems={8}
        rightBlockItems={5}
      />
    );
  };

  const onPreviousScreenDiscardAlert = () => {
    setPreviousScreenDiscardAlert(false);
  };
  return (
    <>
      <div ref={parentContainerRef} onClick={handleClickOutside}>
        <Modal
          dsVersion={"2.0"}
          shouldCloseOnEsc={true}
          shouldCloseOnOverlayClick={false}
          hasOverlay={true}
          onClose={() => {
            onClose();
          }}
          width="90%"
          height="90%"
          contentRef={contentRef}
        >
          <Modal.Header>
            <div
              style={
                isNewGoalTaggingEnabledAndResourceTagged
                  ? styles.modalHeaderV2
                  : styles.modalHeader
              }
            >
              <div>
                <div className={"heading-4"}>
                  {isNewGoalTaggingEnabledAndResourceTagged
                    ? t("journal:tag_learning_elements")
                    : t("journal:select_learning_goals")}

                  {isNewGoalTaggingEnabledAndResourceTagged ? (
                    <DSProvider dsVersion="2.0">
                      <div className={classes.navigation}>
                        <Button
                          variant="neutral"
                          type="inline"
                          dsVersion="2.0"
                          onClick={onPreviousOrCancelClick}
                          size="large"
                          disabled={disableUnitNavigation}
                          className={
                            disableUnitNavigation
                              ? classes.buttonPosition
                              : classes.button
                          }
                        >
                          {isAssessment
                            ? t("journal:learning_experience")
                            : t("common:unit")}
                        </Button>
                        {getIsStandardsRevampEnabled() ? (
                          <span className={classes.separator}> /</span>
                        ) : (
                          <IconButton
                            size={"small"}
                            type={"plain"}
                            icon={<ChevronRightOutlined />}
                          />
                        )}
                        <Button
                          variant="progressive"
                          type="inline"
                          size="large"
                          dsVersion="2.0"
                          className={classes.button}
                          style={styles.learningGoalsStyle}
                        >
                          {t("common:learning_goals")}
                        </Button>
                      </div>
                    </DSProvider>
                  ) : null}
                </div>
                {modalHeaderSubTitle ? (
                  <div
                    className={classes.subheaderText}
                    data-test-id={
                      "journal-postcreation-taglearninggoals-source"
                    }
                  >
                    {modalHeaderSubTitle}
                    <span className={classes.postListDot}></span>
                    <span style={styles.postType}>{type}</span>
                  </div>
                ) : null}
              </div>
              {totalCount ? (
                <div
                  className={
                    isNewGoalTaggingEnabledAndResourceTagged
                      ? classes.searchContainer
                      : ""
                  }
                  style={
                    !isNewGoalTaggingEnabledAndResourceTagged
                      ? styles.modalHeaderTitle
                      : BLANK_OBJECT
                  }
                  ref={modalref}
                >
                  {showFilters &&
                  selectFromAllPYP &&
                  isSubjectStandardsFilterEnabled ? (
                    <div className={classes.filterDropdown}>
                      <ChecklistDropdown
                        dsVersion="2.0"
                        options={tagOptions}
                        value={standardsTagFilter || BLANK_ARRAY}
                        onChange={onChangeFilter}
                        showAllOption={true}
                        isSearchEnabled
                      >
                        <FilterLabelButton
                          dsVersion="2.0"
                          variant="neutral"
                          type="outlined"
                          size="medium"
                          label={t("common:tags")}
                          labelValue={getLabelValueForMultiFilterLabelButton({
                            options: tagOptions,
                            selectedOptions: standardsTagFilter || BLANK_ARRAY,
                            t,
                            noneText: t("common:select"),
                            allText: t("common:all"),
                          })}
                        />
                      </ChecklistDropdown>
                    </div>
                  ) : null}
                  <div
                    className={
                      isNewGoalTaggingEnabledAndResourceTagged
                        ? classes.searchBarV2
                        : classes.searchBar
                    }
                  >
                    <SearchInput
                      dsVersion={isNewGoalTaggingEnabled ? "2.0" : "1.0"}
                      focusSearchBySlash={isNewGoalTaggingEnabled}
                      placeholder={t("journal:search_learning_goals")}
                      onChange={setSearchText}
                      value={searchText}
                      testId={"journal-postcreation-taglearning"}
                      size="medium"
                    />
                  </div>
                </div>
              ) : null}
            </div>
          </Modal.Header>
          <Modal.Body style={styles.modalBody}>
            <TagLearningGoalsList
              updateRef={listRef}
              selectElementsRef={selectElementsRef}
              {...props}
              updateSelectedElementsCount={updateSelectedElementsCount}
              searchTerm={searchText}
              selectAllChecked={isSelectAllChecked}
              updateTotalCount={updateTotalCount}
              updateStateChange={updateStateChange}
              isStateChanged={isStateChanged}
              containerRef={modalref}
              updateSearchText={updateSearchText}
              setStandardFilters={setStandardFilters}
              standardFilters={standardFilters || DEFAULT_STANDARD_FILTERS} // Add fallback
              filtersRef={filtersRef}
              updateShowFilters={updateShowFilters}
              showFilters={showFilters}
              customLoadingComponent={
                getIsStandardsRevampEnabled() ? (
                  <CustomLoadingComponent />
                ) : null
              }
            />
          </Modal.Body>
          <Modal.Footer>
            <div className={classes.footer}>
              <div className={classes.footerLeftContainer}>
                {totalCount > 0 ? (
                  <>
                    <div className={classes.selectAllContainer}>
                      <div className={classes.checkBoxContainer}>
                        <Checkbox
                          isChecked={
                            selectedElementsCount === totalCount &&
                            totalCount > 0
                          }
                          onClick={() => {
                            setIsSelectAllChecked(!isSelectAllChecked);
                          }}
                          testId="journal-postcreation-select-all-goals"
                          isIndeterminate={
                            selectedElementsCount > 0 &&
                            selectedElementsCount < totalCount
                          }
                        />
                      </div>
                      <div
                        className={"text-body-1"}
                        style={styles.modalFooterText(selectedElementsCount)}
                      >
                        {selectLEFooterText}
                      </div>
                    </div>
                    <div
                      className={classes.selectedCount}
                      data-test-id="journal-postcreation-tagged-goals-count"
                    >
                      {selectedElementsCount
                        ? t("journal:learning_goals_tagged", {
                            count: selectedElementsCount,
                          })
                        : null}
                    </div>
                  </>
                ) : null}
              </div>
              <div className={classes.footerContainer}>
                {!isNewGoalTaggingEnabled ? (
                  <Button
                    variant={"outlined-subtle"}
                    size="large"
                    style={{
                      marginRight: "16px",
                    }}
                    onClick={onPreviousOrCancelClick}
                    disabled={false}
                    dsVersion="2.0"
                    testId={"journal-postcreation-tagged-goals-back"}
                  >
                    {!!unitPlan || !!resourceItem
                      ? t("common:previous")
                      : t("common:cancel")}
                  </Button>
                ) : null}
                <Button
                  variant={"progressive"}
                  size="large"
                  onClick={() => {
                    onNextClick();
                  }}
                  disabled={!totalCount}
                  testId={"journal-postcreation-tagged-goals-done"}
                  dsVersion="2.0"
                >
                  {isNewGoalTaggingEnabled
                    ? t("journal:select_and_add")
                    : t("common:select")}
                </Button>
              </div>
            </div>
          </Modal.Footer>
        </Modal>
        <Modal
          dsVersion="2.0"
          width={"440px"}
          height={"fit-content"}
          hasOverlay={true}
          shouldCloseOnOverlayClick={true}
          shouldCloseOnEsc={true}
          onClose={() => {
            setAlert(false);
          }}
          isOpen={showAlert}
        >
          <Modal.Header title={t("common:discard_changes")} isClosable={true} />
          <Modal.Body style={styles.configModalBody}>
            <div className={classes.configModalBody}>
              {t("journal:goals_discard_alert_body_text")}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <div className={classes.deleteModalFooter}>
              <Button
                dsVersion={"2.0"}
                onClick={closeAlert}
                type={"outlined"}
                variant="neutral"
                size="medium"
              >
                {t("journal:continue_editing")}
              </Button>
              <Button
                dsVersion="2.0"
                onClick={onCloseModal}
                variant={"destructive"}
                type="fill"
                size="medium"
              >
                {t("common:discard_changes")}
              </Button>
            </div>
          </Modal.Footer>
        </Modal>
      </div>
      <Modal
        dsVersion="2.0"
        width={"440px"}
        height={"fit-content"}
        hasOverlay={true}
        shouldCloseOnOverlayClick={true}
        shouldCloseOnEsc={true}
        onClose={() => {
          setDiscardAlert(false);
        }}
        isOpen={showDiscardAlert}
        zIndex={2500}
      >
        <Modal.Header
          title={
            isNewGoalTaggingEnabled
              ? t("journal:discard_learning_goals")
              : t("common:discard_changes")
          }
          isClosable={true}
        />
        <Modal.Body style={styles.configModalBody}>
          <div className={classes.configModalBody}>
            {isNewGoalTaggingEnabled
              ? t("journal:selecting_unit_will_remove_learning_goals")
              : t("journal:discard_learning_goals_changes_subtext")}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className={classes.deleteModalFooter}>
            <Button
              dsVersion="2.0"
              onClick={e => closeDiscardAlert(e)}
              type={"outlined"}
              variant="neutral"
              size="medium"
            >
              {isNewGoalTaggingEnabled ? t("common:cancel") : t("common:keep")}
            </Button>
            <Button
              dsVersion="2.0"
              onClick={onClickDisacardAndStartOver}
              variant={"destructive"}
              type="fill"
              size="medium"
            >
              {isNewGoalTaggingEnabled
                ? t("journal:discard_and_startover")
                : t("common:discard_and_exit")}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      <Modal
        width={"440px"}
        height={"fit-content"}
        hasOverlay={true}
        shouldCloseOnOverlayClick={true}
        shouldCloseOnEsc={true}
        onClose={() => {
          setPreviousScreenDiscardAlert(false);
        }}
        isOpen={showPreviousScreenDiscardAlert}
        dsVersion="2.0"
      >
        <Modal.Header title={t("common:discard_changes")} isClosable={true} />
        <Modal.Body style={styles.configModalBody}>
          <div className={classes.configModalBody}>
            {t("journal:discard_tagged_learning_goals_changes")}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className={classes.deleteModalFooter}>
            <Button
              dsVersion="2.0"
              onClick={onPreviousScreenDiscardAlert}
              size="large"
              variant="outlined-subtle"
            >
              {t("common:keep")}
            </Button>
            <Button
              dsVersion="2.0"
              onClick={onPreviousButtonClick}
              size="large"
            >
              {t("journal:discard_and_go_back")}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
}

const mapActionCreators = {
  setToastMsg,
};

const getModalHeaderTitle = ({ resourceItem, unitPlan, selectFromAllPYP }) => {
  let modalHeaderTitle = "journal:tag_PYP_elements_stage";
  let modalHeaderSubTitle = "";
  let type = "";

  if (selectFromAllPYP) {
    modalHeaderTitle = "journal:tag_from_all_le_goals";
  } else if (!_.isEmpty(resourceItem)) {
    _.get(resourceItem, ["assessmentType", "value"]) === "le"
      ? (modalHeaderTitle = "journal:tag_learning_goals_from_learning_exp")
      : (modalHeaderTitle = "journal:tag_learning_goals_from_assessment");
    modalHeaderSubTitle = _.get(resourceItem, "title.value", "");
    type = "Learning experience";
  } else if (!_.isEmpty(unitPlan)) {
    modalHeaderTitle = "journal:tag_learning_goals_from_unit";
    modalHeaderSubTitle = _.get(unitPlan, "title.value", "");
    type = "Unit";
  }
  return { modalHeaderTitle, modalHeaderSubTitle, type };
};
const mapStateToProps = (state, ownProps) => {
  const { goToPreviousScreen = () => {} } = _.get(ownProps, "props", {});
  const {
    resourceItem,
    unitPlan,
    selectFromAllPYP,
    selectFromPost,
    postTaggedEntity,
    elementPYP,
  } = state.post.postDetails;
  const { modalHeaderTitle, modalHeaderSubTitle, type } = getModalHeaderTitle({
    resourceItem,
    unitPlan,
    selectFromAllPYP,
  });
  const standardsTagFilter = _.get(
    state,
    "post.postDetails.standardsTagFilter"
  );
  const isPostCreationModeNew =
    !_.size(_.keys(_.get(state, "post.postDetails.createdBy"))) > 0;
  return {
    curriculumProgramType: _.get(
      state,
      "platform.currentCurriculumProgram.type",
      ""
    ),
    modalHeaderTitle,
    modalHeaderSubTitle,
    selectFromAllPYP,
    unitPlan,
    resourceItem,
    type,
    selectFromPost,
    goToPreviousScreen,
    isPostCreationModeNew,
    postTaggedEntity,
    elementPYP,
    standardsTagFilter,
  };
};

export default compose(connect(mapStateToProps, mapActionCreators))(
  TagLearningGoals
);
